/**
 * Constantes del dominio de Inventario: permisos por acción (espejo de la
 * API/SSR), catálogo de calibres/formatos/perfiles y diccionario de errores.
 *
 * El gating de la UI usa los MISMOS permisos que exige la API (`application/api/
 * domains/inventario.py`) y el SSR (`inventario_controller.py`):
 *   - ver rollos / estado:       `view_inventory`
 *   - añadir / consumir / borrar: `manage_inventory`
 *   - "Activar por sede" (admin): rol `admin` (la API exige el permiso `all`).
 *
 * El catálogo (calibres galvanizada, formatos, perfiles de acero/aluminio con su
 * kg/m) replica el de `InventarioService` (`CALIBRES_INV`/`PERFILES_INV`) para
 * que el formulario de alta ofrezca exactamente las mismas opciones que el
 * backend acepta, sin inventar referencias.
 */
import type { RolloTipo } from "./types";

/** Permiso para ver el inventario (rollos + estado). Igual que el menú y la API. */
export const VIEW_PERMISSION = "view_inventory";
/** Permiso para gestionar el inventario (añadir/consumir/borrar rollos). */
export const MANAGE_PERMISSION = "manage_inventory";

// ── Catálogo galvanizada (espejo de CALIBRES_INV en inventario_service.py) ────

/** Un calibre de lámina galvanizada con su kg por lámina por formato. */
export interface CalibreGalv {
  /** Calibre (clave de la rama `galv` del payload, p. ej. "20"). */
  cal: string;
  /** Etiqueta legible (p. ej. "Cal. 20"). */
  label: string;
  /** kg por lámina en formato 4x8 (para estimar nº de láminas al consumir). */
  p4x8: number;
  /** kg por lámina en formato 1x2. */
  p1x2: number;
}

/** Calibres galvanizada del backend (orden idéntico a `CALIBRES_INV`). */
export const CALIBRES_GALV: CalibreGalv[] = [
  { cal: "16", label: "Cal. 16", p4x8: 34.14, p1x2: 22.9 },
  { cal: "18", label: "Cal. 18", p4x8: 27.06, p1x2: 18.18 },
  { cal: "20", label: "Cal. 20", p4x8: 19.93, p1x2: 13.39 },
  { cal: "22", label: "Cal. 22", p4x8: 16.39, p1x2: 11.01 },
  { cal: "24", label: "Cal. 24", p4x8: 12.84, p1x2: 8.63 },
  { cal: "26", label: "Cal. 26", p4x8: 10.07, p1x2: 6.99 },
];

/** Formatos de lámina galvanizada (las dos ramas que agrupa el backend). */
export const FORMATOS_GALV = ["4x8", "1x2"] as const;
export type FormatoGalv = (typeof FORMATOS_GALV)[number];

// ── Catálogo acero/aluminio (espejo de PERFILES_INV en inventario_service.py) ─

/** Un perfil de acero/aluminio con su kg por metro lineal. */
export interface PerfilAcero {
  /** Id del perfil (clave de la rama `acero` del payload, p. ej. "c20_100"). */
  id: string;
  /** Etiqueta legible (p. ej. "C.20 × 1,00"). */
  label: string;
  /** kg por metro (para estimar el peso al consumir). */
  kgm: number;
  /** Grupo para agrupar el selector (p. ej. "ACEROS Cal 20"). */
  grupo: string;
}

/** Perfiles de acero/aluminio del backend (orden idéntico a `PERFILES_INV`). */
export const PERFILES_ACERO: PerfilAcero[] = [
  { id: "c20_100", label: "C.20 × 1,00", kgm: 6.68, grupo: "ACEROS Cal 20" },
  { id: "c20_100pvc", label: "C.20 × 1,00 PVC", kgm: 6.68, grupo: "ACEROS Cal 20" },
  { id: "c20_120", label: "C.20 × 1,20", kgm: 8.01, grupo: "ACEROS Cal 20" },
  { id: "c20_120pvc", label: "C.20 × 1,20 PVC", kgm: 8.01, grupo: "ACEROS Cal 20" },
  { id: "c20_100sat", label: "C.20 × 1,00 PVC SAT", kgm: 6.68, grupo: "ACEROS Cal 20" },
  { id: "c20_120sat", label: "C.20 × 1,20 PVC SAT", kgm: 8.01, grupo: "ACEROS Cal 20" },
  { id: "c22_100", label: "C.22 × 1,00", kgm: 5.5, grupo: "ACEROS Cal 22" },
  { id: "c22_120", label: "C.22 × 1,20", kgm: 6.6, grupo: "ACEROS Cal 22" },
  { id: "c22_100pvc", label: "C.22 × 1,00 PVC", kgm: 5.5, grupo: "ACEROS Cal 22" },
  { id: "c22_100sat", label: "C.22 × 1,00 PVC SAT", kgm: 5.5, grupo: "ACEROS Cal 22" },
  { id: "c22_120pvc", label: "C.22 × 1,20 PVC", kgm: 6.6, grupo: "ACEROS Cal 22" },
  { id: "c22_120sat", label: "C.22 × 1,20 PVC SAT", kgm: 6.6, grupo: "ACEROS Cal 22" },
  { id: "c24_100", label: "C.24 × 1,00", kgm: 4.32, grupo: "ACEROS Cal 24" },
  { id: "c24_120", label: "C.24 × 1,20", kgm: 5.18, grupo: "ACEROS Cal 24" },
  { id: "c24_100pvc", label: "C.24 × 1,00 PVC", kgm: 4.32, grupo: "ACEROS Cal 24" },
  { id: "c24_120pvc", label: "C.24 × 1,20 PVC", kgm: 5.18, grupo: "ACEROS Cal 24" },
  { id: "c24_100sat", label: "C.24 × 1,00 PVC SAT", kgm: 4.32, grupo: "ACEROS Cal 24" },
  { id: "c24_120sat", label: "C.24 × 1,20 PVC SAT", kgm: 5.18, grupo: "ACEROS Cal 24" },
  { id: "c26_100", label: "C.26 × 1,00", kgm: 3.38, grupo: "ACEROS Cal 26" },
  { id: "c26_120", label: "C.26 × 1,20", kgm: 4.05, grupo: "ACEROS Cal 26" },
  { id: "c26_100pvc", label: "C.26 × 1,00 PVC", kgm: 3.38, grupo: "ACEROS Cal 26" },
  { id: "c26_120pvc", label: "C.26 × 1,20 PVC", kgm: 4.05, grupo: "ACEROS Cal 26" },
  { id: "c28_100", label: "C.28 × 1,00", kgm: 2.97, grupo: "ACEROS Cal 28" },
  { id: "c28_100pvc", label: "C.28 × 1,00 PVC", kgm: 2.97, grupo: "ACEROS Cal 28" },
  { id: "c28_120", label: "C.28 × 1,20", kgm: 3.56, grupo: "ACEROS Cal 28" },
  { id: "c30_100", label: "C.30 × 1,00", kgm: 2.38, grupo: "ACEROS Cal 30" },
  { id: "alu04", label: "ALU 0,4", kgm: 1.08, grupo: "ALUMINIO" },
  { id: "alu05", label: "ALU 0,5", kgm: 1.35, grupo: "ALUMINIO" },
  { id: "alu07", label: "ALU 0,7", kgm: 1.89, grupo: "ALUMINIO" },
  { id: "alu09", label: "ALU 0,9", kgm: 2.43, grupo: "ALUMINIO" },
];

/** Etiqueta legible del tipo de rollo (para encabezados). */
export const TIPO_LABEL: Record<RolloTipo, string> = {
  galv: "Galvanizada (kg)",
  acero: "Acero / Aluminio (m)",
};

/** Busca un calibre galvanizado por su clave (o `undefined`). */
export function findCalibreGalv(cal: string): CalibreGalv | undefined {
  return CALIBRES_GALV.find((c) => c.cal === cal);
}

/** Busca un perfil de acero/aluminio por su id (o `undefined`). */
export function findPerfilAcero(id: string): PerfilAcero | undefined {
  return PERFILES_ACERO.find((p) => p.id === id);
}

/**
 * Diccionario de mensajes legibles por `error_code` de la API de inventario
 * (`_SERVICE_ERROR_CODES` en `inventario.py`). Si llega un código no listado, la
 * UI cae al `message` del propio `ApiError` (que el backend ya da en español, con
 * el texto exacto del service). Por eso este mapa es mínimo.
 */
export const INVENTARIO_ERROR_MESSAGE: Record<string, string> = {
  forbidden: "No tienes permiso para realizar esta acción.",
  not_found: "El rollo no existe o pertenece a otra sede.",
  validation_error: "Revisa los datos del rollo.",
  internal_error: "Ocurrió un error inesperado. Inténtalo de nuevo.",
};
